import ButtonComponent from 'app_components/commons/Button/ButtonComponent'
import TextComponent from 'app_components/commons/TextComponent/TextComponent'
import TermService from 'app_services/admin/term/termService'
import { ITerm, Terms } from 'app_types/admin/term/term.types'
import React, { FC, useEffect, useState } from 'react'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import { IoMdAdd } from 'react-icons/io'
import TermAdminCard from './components/TermAdminCard/TermAdminCard'
import TermForm from './components/TermForm/TermForm'

import './TermListModule.scss'

interface ITermListModuleProps {
  type: Terms
  title?: string
  description?: string
  newButtonText?: string
  onChangeItems?: (items: ITerm[]) => void
}

const TermListModule: FC<ITermListModuleProps> = ({ type, title, description, newButtonText, onChangeItems }) => {
  const [showNewForm, setShowNewForm] = useState<boolean>(false)
  const [terms, setTerms] = useState<ITerm[]>([])
  const termService = new TermService()

  useEffect(() => {
    if (terms?.length) {
      onChangeItems && onChangeItems(terms)
    } else {
      onChangeItems && onChangeItems([])
    }
  }, [onChangeItems, terms])

  const getTerms = async () => {
    const response = await termService.getByType({ type })
    if (response?.code === 200 && response?.terms?.length) {
      setTerms(response.terms)
    } else {
      setTerms([])
    }
  }

  useEffect(() => {
    getTerms()
  }, [])

  const handleTermCreated = () => {
    setShowNewForm(false)
    getTerms()
  }

  const handleCancelCreate = () => {
    setShowNewForm(false)
  }

  const handleClickNew = () => {
    setShowNewForm(true)
  }

  const handleDragEnd = async ({ source, destination }: DropResult) => {
    if (!source || !destination) return
    const newItems = [...terms]
    const [removed] = newItems.splice(source.index, 1)
    newItems.splice(destination.index, 0, removed)
    const sortedItems = newItems.map((item, idx) => ({...item, position: idx}))
    setTerms(sortedItems)
    await termService.updateMany({ terms: sortedItems.map((item) => ({ ...item, id: item._id })) })
    getTerms()
  }

  return (
    <div className="term-list-module--layout">
      {!!title?.length && (
        <TextComponent type="subtitle">
          {title}
        </TextComponent>
      )}
      {!showNewForm ? (
        <ButtonComponent onClick={handleClickNew} variant="primary" width="fit-content">
          <ButtonComponent.Icon>
            <IoMdAdd />
          </ButtonComponent.Icon>
          {newButtonText ? newButtonText : 'Nuevo'}
        </ButtonComponent>
      ) : (
        <TermForm
          type={type}
          position={terms?.length}
          onTermCreated={handleTermCreated}
          onCancel={handleCancelCreate}
        />
      )}
      {!!terms?.length && !!description?.length && (
        <TextComponent>
          {description}
        </TextComponent>
      )}
      <DragDropContext
        onDragEnd={handleDragEnd}
      >
        <Droppable
          droppableId={`droppable-container-terms-${type}`}
        >
          {({ droppableProps, innerRef }) => (
            <div
              {...droppableProps}
              ref={innerRef}
              className='term-list-module--list'
            >
              {terms?.map((term, idx) => (
                <Draggable
                  key={term._id}
                  draggableId={term._id}
                  index={idx}
                >
                  {(provided) => (
                    <TermAdminCard
                      term={term}
                      onTermDeleted={getTerms}
                      onTermUpdated={getTerms}
                      provided={provided}
                    />
                  )}
                </Draggable>
              ))}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  )
}

export default TermListModule
